import { MODAL_STATE, type ITEM_LAYOUT } from 'tradera/constants/ui';
import { nullifyUndefinedProperties } from 'tradera/utils/object';
import { DEFAULT_COOKIES } from 'tradera/constants/cookies';
import cookieUtil from 'tradera/utils/cookie';
import { cache, CACHE_KEYS } from 'tradera/utils/cache';
import { initialize } from './reducer';
import { type Dispatch } from 'redux';
import { type InitData } from '../init-data/types';

export type UiState = {
    categoryDropdownMenu: {
        open: MODAL_STATE;
    };
    badges: {
        messaging: {
            threads: number;
            threadsUnread: number;
        };
        system: number;
    };
    itemLayout: ITEM_LAYOUT;
    itemSettings: {
        open: MODAL_STATE;
        item: unknown;
    };
    messagingModal: {
        open: MODAL_STATE;
        title: string;
        src: string;
        participating: boolean;
        recipientId: string;
        item: unknown;
        isContactHighestBidder: boolean;
    };
    modals: {
        currentModal: unknown;
        currentModalData: unknown;
    };
    notificationOptinModal: {
        open: MODAL_STATE;
        modalType: string;
        gaLabel: string;
        title: string;
        description: string;
        tagGroups: unknown[];
    };
    profileMenu: {
        open: MODAL_STATE;
        redirectUrl: boolean;
    };
    quickView: {
        open: MODAL_STATE;
        items: unknown[];
        item: unknown;
        loading?: boolean;
    };
    siteLayout: string;
    showHeaderSearch: boolean;
    showSaveSearchBanner: boolean;
    showSearchForm: boolean;
    systemMessages: {
        BankAccountMissing: boolean;
        InvoiceOverdue: boolean;
        InvoiceUnpaid: boolean;
        PayoutFailed: boolean;
        PayoutReadyBankAccountMissing: boolean;
    };
    videoModal: {
        open: MODAL_STATE;
        videoUrl: string;
        title: string;
        pageType: string;
        buttonLink: string;
        buttonText: string;
    };
    youtubeVideoModal: {
        open: MODAL_STATE;
        videoId: string;
        title: string;
        pageType: string;
    };
    shareModal: {
        isOpen: boolean;
        settings: unknown;
    };
    wishListSideSheet: {
        isOpen: boolean;
    };
    productCommunications: unknown;
    collectSsnModal: {
        isOpen: boolean;
    };
    messageThreadCount: number;
    messageThreadsUnreadCount: number;
    showCollectSsnModal: boolean;
    helpSection: {
        isOpen: boolean;
        settings: unknown;
    };
};

export const getInitialBadges = () => ({
    messaging: {
        threads: 0,
        threadsUnread: 0
    },
    system: 0
});

export const getInitialState = ({
    itemLayout,
    messageThreadCount = 0,
    messageThreadsUnreadCount = 0,
    showHeaderSearch,
    showSearchForm,
    siteLayout,
    systemMessages,
    productCommunications,
    showCollectSsnModal
}: Partial<UiState>) => {
    const badges = getInitialBadges();
    // eslint-disable-next-line better-mutation/no-mutation
    badges.messaging.threads = messageThreadCount;
    // eslint-disable-next-line better-mutation/no-mutation
    badges.messaging.threadsUnread = messageThreadsUnreadCount;

    return nullifyUndefinedProperties({
        categoryDropdownMenu: { open: MODAL_STATE.CLOSED },
        badges,
        itemLayout: itemLayout,
        itemSettings: {
            open: MODAL_STATE.CLOSED,
            item: {}
        },
        messagingModal: {
            open: MODAL_STATE.CLOSED,
            title: '',
            src: '',
            participating: false,
            recipientId: '',
            item: null
        },
        modals: {
            currentModal: null
        },
        notificationOptinModal: {
            open: MODAL_STATE.CLOSED,
            modalType: null,
            gaLabel: null,
            title: null,
            description: null,
            tagGroups: []
        },
        profileMenu: { open: MODAL_STATE.CLOSED, redirectUrl: false },
        quickView: {
            open: MODAL_STATE.CLOSED,
            items: [],
            item: null,
            loading: false
        },
        siteLayout,
        showHeaderSearch,
        showSearchForm,
        systemMessages: {
            BankAccountMissing: false,
            InvoiceOverdue: false,
            InvoiceUnpaid: false,
            PayoutFailed: false,
            PayoutReadyBankAccountMissing: false,
            OutstandingTraderaPayLicense: false,
            PayoutReadyOutstandingTraderaPayLicense: false,
            ...systemMessages
        },
        videoModal: {
            open: MODAL_STATE.CLOSED,
            video: ''
        },
        youtubeVideoModal: {
            open: MODAL_STATE.CLOSED,
            video: ''
        },
        shareModal: {
            isOpen: false,
            settings: null
        },
        wishListSideSheet: {
            isOpen: false
        },
        productCommunications,
        collectSsnModal: {
            isOpen: !!showCollectSsnModal
        }
    });
};

type MessagingBadgesFromCache = {
    threads: number;
    threadsUnread: number;
};

export const initializeFromInitData =
    (initData: Partial<InitData>) => (dispatch: Dispatch) => {
        const messagingBadges =
            cache.read(CACHE_KEYS.MESSAGING_BADGES) ||
            ({
                threads: 0,
                threadsUnread: 0
            } as MessagingBadgesFromCache);

        const state = getInitialState({
            itemLayout:
                cookieUtil.readCookie(DEFAULT_COOKIES['ITEM_LAYOUT'].key) ||
                DEFAULT_COOKIES['ITEM_LAYOUT'].default,
            messageThreadCount: messagingBadges.threads,
            messageThreadsUnreadCount: messagingBadges.threadsUnread,
            showHeaderSearch:
                typeof initData.pageName === 'string' &&
                initData.pageName.includes('StartPage'),
            showSearchForm: !!initData.hasSearch,
            siteLayout:
                cookieUtil.readCookie(DEFAULT_COOKIES['SITE_LAYOUT'].key) ||
                DEFAULT_COOKIES['SITE_LAYOUT'].default,
            systemMessages: cache.read(CACHE_KEYS.SYSTEM_MESSAGES) || {},
            productCommunications: initData.productCommunications,
            showCollectSsnModal:
                initData.memberCountryCodeIso2 === 'SE' &&
                !initData.isCompany &&
                !initData.hasSsn
        });
        dispatch(initialize(state));
    };
